import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const navTextClass = scrolled ? 'text-foreground-950' : 'text-white';
  const pillClass = scrolled
    ? 'bg-background-100 text-foreground-800 hover:bg-background-200'
    : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm';
  const ctaClass = scrolled
    ? 'bg-primary-500 text-white hover:bg-primary-600'
    : 'bg-white text-foreground-900 hover:bg-background-100';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-xl font-bold whitespace-nowrap transition-colors ${navTextClass}`}
          >
            KakobuySpreadsheet<span className="text-accent-500">.</span>cc
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-3 md:gap-5">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-label whitespace-nowrap cursor-pointer transition-all ${pillClass}`}
              >
                <span>Kategorie</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)}></div>
                  <div className="absolute right-0 top-full mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 z-20 py-2 max-h-[420px] overflow-y-auto shadow-lg">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="font-label font-bold">{cat.namePl}</span>
                        <span className="text-foreground-500 ml-2 text-xs">{cat.nameEn}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-label font-bold whitespace-nowrap cursor-pointer transition-all ${ctaClass}`}
            >
              Kakobuy Sheet
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all ${pillClass}`}
            aria-label="Otwórz menu"
          >
            <i className="ri-menu-line text-lg"></i>
          </button>
        </div>
      </nav>

      {/* Mobile overlay menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-background-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-background-200/70">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="font-heading text-xl font-bold text-foreground-950 whitespace-nowrap"
            >
              KakobuySpreadsheet<span className="text-accent-500">.</span>cc
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-all"
              aria-label="Zamknij menu"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            {/* Main CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary-500 text-white font-label font-bold text-sm rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all mb-8"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line"></i>
              </span>
              Kakobuy Sheet — Buy It Now
            </a>

            {/* Section label */}
            <p className="text-xs font-label uppercase tracking-wider text-foreground-400 mb-4">
              Kategorie Produktów
            </p>

            {/* Category grid — 2 columns on mobile for easy tapping */}
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex flex-col items-start gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span className="font-label font-bold text-sm text-foreground-900 leading-tight">
                    {cat.namePl}
                  </span>
                </a>
              ))}
            </div>

            {/* Quick links */}
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <p className="text-xs font-label uppercase tracking-wider text-foreground-400 mb-4">
                Przydatne Linki
              </p>
              <div className="flex flex-col gap-2">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    <i className="ri-home-line"></i>
                  </span>
                  Strona Główna
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    <i className="ri-external-link-line"></i>
                  </span>
                  Główna Strona Zakupowa
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}